import { Injectable, computed, inject, signal } from '@angular/core';
import {
  catchError,
  filter,
  finalize,
  map,
  of,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs';

import { MyResumeGQL } from '@cpd-app/generated';
import { Apollo, gql } from 'apollo-angular';

/**
 * T-2. The learner's own KSAs, for the skills offer.
 *
 * HAND-WRITTEN until the next codegen run, for the reason the other two
 * hand-written documents in this codebase give: codegen reads its schema from
 * a RUNNING gateway, so a field cannot reach generated.ts before the subgraph
 * carrying it is deployed.
 */
const MY_RECORD_SKILLS = gql`
  query MyRecordSkills {
    myRecordSkills
  }
`;

import { AuthFacade } from '@cpd-app/state/auth/auth.facade';

import { AceApiService } from '../ace/ace-api.service';
import { JstApiService } from '../jst/jst-api.service';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
import { isCivilianMocMarker } from '@cpd-app/shared-module/constants/member-identity.const';

/**
 * One entry the record already holds, in the shape a portfolio section's
 * fields take. Deliberately the same four keys the field table uses, so
 * offering one is a copy rather than a translation.
 */
export interface RecordEntry {
  title: string;
  place?: string;
  year?: string;
  note?: string;
  /** Education only: the level, one of EDUCATION_LEVELS. */
  level?: string;
  /**
   * T.7. The parts of a JST occupation that fill a work row completely.
   *
   * `place` and `note` keep their portfolio meaning (the years, and the
   * description) so the portfolio is unchanged. These are what the resume's
   * work repeater needs on top: months for its month controls, the branch as
   * the employer, and the description as the first line under the job. Only
   * the JST sets them; a survey row is still a title and nothing else.
   */
  start?: string;
  end?: string;
  current?: boolean;
  employer?: string;
  line?: string;
}

/**
 * The first seven characters of an ISO date, which is what a month control
 * holds. Anything that is not at least YYYY-MM is dropped rather than padded:
 * a year alone does not say which month, and inventing January would put a
 * wrong date on a document sent to an employer.
 */
function toMonth(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();

  return /^\d{4}-\d{2}/.test(trimmed) ? trimmed.slice(0, 7) : '';
}

/**
 * The branch as a resume names an employer. The transcript says "Air Force";
 * a civilian reader expects "U.S. Air Force". Anything already carrying the
 * prefix is left alone.
 */
function branchAsEmployer(branch: string | null | undefined): string {
  const trimmed = (branch ?? '').trim();

  if (!trimmed) {
    return '';
  }

  return /^u\.?s\.?\s/i.test(trimmed) ? trimmed : `U.S. ${trimmed}`;
}

/**
 * First entry wins for any repeated title.
 *
 * The same qualification reaches this service from several directions -- a JST
 * course the member also typed into their resume, a MOC that is both on the
 * profile and on the transcript -- and offering it twice makes the product look
 * like it cannot count. Callers order their inputs richest-first, so the copy
 * that survives is the one carrying a place and a year rather than a bare
 * title.
 *
 * Case- and space-insensitive, because "Basic Leader Course" and "basic leader
 * course " are the same course to everybody except a Set.
 */
/**
 * The levels an Education entry can be, in the order a reader ranks them.
 * Jackson, 28 Sep: "Cyber/Computer Forensics and Counterterrorism" arrived with
 * no level, so nobody could tell a bachelor's in progress from a certificate.
 */
export const EDUCATION_LEVELS: string[] = [
  'High school diploma or GED',
  // T.10 S6 (spec 9.7, D16): what was studied, never a missing degree.
  'College coursework',
  'Certificate',
  "Associate's degree",
  "Bachelor's degree",
  "Master's degree",
  'Doctorate',
  'Professional degree',
  'Military training'
];

/**
 * T.10 S6. The words this level used to have. Resumes saved before, and the
 * T.8 import (its prompt is unchanged), still carry them; they read as
 * "College coursework" so no resume or page says "no degree" (D16).
 */
export const OLD_COLLEGE_LEVEL = 'Some college, no degree yet';

/** What "Year finished" says for schooling not finished yet. */
export const IN_PROGRESS = 'In progress';

/**
 * A Skills Survey level ("Some College Courses", "Bachelor's Degree") as one of
 * EDUCATION_LEVELS, or the survey's own words when nothing matches, so a level
 * is never dropped for want of a mapping.
 */
export function educationLevelFor(survey: string | null | undefined): string {
  const said = (survey ?? '').trim();
  const s = said.toLowerCase();
  if (!s) {
    return '';
  }
  // T.10 S6 review M4: the partial levels first, so none reads as a credential
  // not held ("Less than High School" is not a diploma, "Some Master's
  // Courses" not a master's, "Post-Doctoral Training" not a doctorate). A
  // null level keeps the survey's own words.
  const rules: [RegExp, string | null][] = [
    [/less than/, ''],
    [/post-?doctoral/, null],
    [/^some\b|coursework|no degree/, EDUCATION_LEVELS[1]],
    [/ccaf degree/, EDUCATION_LEVELS[3]],
    [/high school|ged|diploma/, EDUCATION_LEVELS[0]],
    [/certificate|certification/, EDUCATION_LEVELS[2]],
    [/associate/, EDUCATION_LEVELS[3]],
    [/bachelor/, EDUCATION_LEVELS[4]],
    [/master/, EDUCATION_LEVELS[5]],
    [/doctor|ph\.?d/, EDUCATION_LEVELS[6]],
    [/professional/, EDUCATION_LEVELS[7]]
  ];
  const hit = rules.find(([re]) => re.test(s));
  return hit ? hit[1] ?? said : said;
}

/**
 * An Education entry as a reader sees it: "Bachelor's degree, Cyber/Computer
 * Forensics and Counterterrorism". The level is left off when the credential
 * already says it ("High School Diploma").
 */
export function educationTitle(
  credential: string,
  level?: string | null
): string {
  const what = (credential ?? '').trim();
  const held = (level ?? '').trim();
  const lvl = held.toLowerCase() === OLD_COLLEGE_LEVEL.toLowerCase() ? EDUCATION_LEVELS[1] : held;
  if (!lvl) {
    return what;
  }
  if (!what) {
    return lvl;
  }
  const first = lvl.split(/[\s,']/)[0].toLowerCase();
  if (what.toLowerCase().includes(first)) {
    return what;
  }
  // T.10 S6 (spec 9.7): "College coursework in Information Technology".
  if (lvl === EDUCATION_LEVELS[1]) {
    return `${lvl} in ${what}`;
  }
  // A degree or certificate leads; anything else (military training) follows
  // the program in brackets.
  return /degree$|^certificate$|^doctorate$/i.test(lvl)
    ? `${lvl}, ${what}`
    : `${what} (${lvl.charAt(0).toLowerCase()}${lvl.slice(1)})`;
}

function titleKey(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * MOST RECENT FIRST, the order a resume reads in. A JST lists oldest first.
 * Sorted on the start month, else the year; anything undated keeps its place
 * after everything dated (the sort is stable).
 */
export function newestFirst(entries: RecordEntry[]): RecordEntry[] {
  const when = (entry: RecordEntry): string =>
    entry.start ||
    (entry.year ?? '').trim() ||
    (entry.place ?? '').match(/\d{4}/)?.[0] ||
    '';

  return [...entries].sort((a, b) => {
    const [x, y] = [when(a), when(b)];
    if (!x || !y) {
      return x ? -1 : y ? 1 : 0;
    }
    return y.localeCompare(x);
  });
}

/**
 * The profile's MOC rows ("09S Commissioned Officer Candidate") that the JST
 * already carries under the same code with a different title ("09S Officer
 * Candidate School"). Matched on the leading code, which is how both spell it.
 */
function notSameCode(
  entries: RecordEntry[],
  jst: RecordEntry[]
): RecordEntry[] {
  const code = (title: string): string => {
    const first = title.trim().split(/\s+/)[0] ?? '';
    return /\d/.test(first) && first.length <= 6 ? first.toUpperCase() : '';
  };
  const held = new Set(jst.map((entry) => code(entry.title)).filter(Boolean));

  return entries.filter((entry) => !held.has(code(entry.title)));
}

/**
 * The work titles the profile holds, military first: a member's own MOC title
 * describes what they did more specifically than the civilian crosswalk title
 * does.
 *
 * WALK-41. The civilian marker row (code "X", title "Learner", see
 * isCivilianMocMarker) is not a job. It was joined into "X Learner" and seeded
 * into a civilian's Work history as an entry they never added.
 */
export function profileWorkTitles(
  mocs: ({ mocCode?: string; mocTitle?: string } | null)[] | null | undefined,
  jobs: ({ occupationTitle?: string } | null)[] | null | undefined
): string[] {
  return [
    ...(mocs ?? [])
      .filter((moc) => !isCivilianMocMarker(moc?.mocCode, moc?.mocTitle))
      .map((moc) =>
        [moc?.mocCode, moc?.mocTitle]
          .map((part) => (part ?? '').trim())
          .filter(Boolean)
          .join(' ')
      ),
    ...(jobs ?? []).map((row) => (row?.occupationTitle ?? '').trim())
  ].filter(Boolean);
}

function dedupe(entries: RecordEntry[]): RecordEntry[] {
  const seen = new Set<string>();

  return entries.filter((entry) => {
    const key = titleKey(entry.title);

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

/**
 * T.8, shared since T.10 S8. The resume's work history as the portfolio's
 * Experience entries: most recent first, employer and years as the quieter
 * line, the job's lines as its note. `applyResume` and the wizard's
 * one-press page (`portfolioFromWizard`) both use it, so they cannot drift.
 */
export function experienceFromResume(document: unknown): RecordEntry[] {
  const doc = (document ?? {}) as {
    rows?: Record<string, Record<string, string>[]>;
    lines?: Record<string, string[]>;
  };
    /*
     * Most recent first on the public page, whatever order the resume holds
     * them in: a current job, then by start month. Undated rows keep their
     * place after the dated ones. The index travels with the row because a
     * job's lines are keyed on it.
     */
    const byRecency = (doc.rows?.['work'] ?? [])
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const when = (r: Record<string, string>): string =>
          r['current'] === 'yes' ? '9999' : (r['start'] ?? '').trim();
        const [x, y] = [when(a.row), when(b.row)];
        if (!x || !y) {
          return x ? -1 : y ? 1 : 0;
        }
        return y.localeCompare(x);
      });

    return byRecency
        .map(({ row, index }) => {
          // A month control holds YYYY-MM, and a reader scanning dates
          // wants the years. Employer and years travel together as the
          // quieter line under the job title, which is how the published
          // page prints an entry.
          // A current job reads "2019 to present", as the resume prints it.
          const years = [
            (row['start'] ?? '').trim().slice(0, 4),
            row['current'] === 'yes'
              ? 'present'
              : (row['end'] ?? '').trim().slice(0, 4)
          ]
            .filter(Boolean)
            .join(' to ');
          const where = [(row['employer'] ?? '').trim(), years]
            .filter(Boolean)
            .join(', ');

          return {
            title: (row['title'] ?? '').trim(),
            place: where,
            // One line per paragraph, kept as the member wrote them.
            note: (doc.lines?.[`work:${index}`] ?? [])
              .map((line) => line.trim())
              .filter(Boolean)
              .join('\n')
          };
        })
        .filter((entry) => entry.title || entry.note);
}

/**
 * What the member's record already holds, offered to the portfolio so a
 * section does not ask them to retype something the product already knows.
 *
 * The design set's rule is that a slot offering to import from your record has
 * to import something real, so each source here is the actual one: credentials
 * come from the uploaded ACE transcripts, and education and skills come from
 * the resume, which is where the member entered them.
 *
 * A failed read is not an empty record. Both would render no offer, but only
 * one of them should be quiet about it, so the failure is kept separate and
 * the section falls back to its own form without claiming the record is bare.
 */

@Injectable({ providedIn: 'root' })
export class PortfolioRecordService {
  private readonly ace = inject(AceApiService);
  private readonly jst = inject(JstApiService);
  private readonly userProfileFacade = inject(UserProfileFacade);
  private readonly myResumeGQL = inject(MyResumeGQL);
  private readonly authFacade = inject(AuthFacade);

  /**
   * Credentials, from the two DIFFERENT transcript uploads CPD accepts.
   *
   * Kept apart rather than merged at the source because they fail
   * independently and are gated differently: the ACE route is refused unless
   * `ACE_TRANSCRIPT_UPLOAD_ENABLED` is on, while the JST route is not gated at
   * all. One being empty says nothing about the other.
   */
  private readonly aceCredentials = signal<RecordEntry[]>([]);
  private readonly jstCredentials = signal<RecordEntry[]>([]);

  /** Work history from the JST, which is the only source carrying real dates. */
  private readonly jstWork = signal<RecordEntry[]>([]);

  /** Titles only, from the surveys. Every other box is deliberately blank. */
  private readonly apollo = inject(Apollo);

  private readonly fromProfile = signal<Record<string, RecordEntry[]>>({});

  private readonly fromResume = signal<Record<string, RecordEntry[]>>({});

  /**
   * The resume's work history, in portfolio-entry shape.
   *
   * Kept apart from `sources` on purpose: Experience is not a section a member
   * imports one row at a time, it IS the resume's work history, so the builder
   * copies the whole list into the document rather than offering it. Without
   * that copy the section published empty and a visitor saw no work history,
   * even though the builder counted the section as filled.
   */
  public readonly experience = signal<RecordEntry[]>([]);

  public readonly hasLoaded = signal(false);

  /**
   * T.7. THE RECORD READS HAVE LANDED, for a caller that never calls `load()`.
   *
   * The resume builder gated its seeding on `hasLoaded`, and only `load()`
   * sets that -- the PORTFOLIO's read, which also fetches `myResume`. The
   * resume calls `loadCredentials()` instead, so a member who opened the
   * resume before the portfolio, which is the order the page presents them
   * in, had a JST on file and an empty Work history: the seed never ran.
   *
   * Set when all four reads `loadCredentials()` starts have finished, each
   * one whether it succeeded or not, because a failed read is still a read
   * that will not be arriving later.
   */
  public readonly credentialsLoaded = signal(false);
  private pendingReads = 0;
  /**
   * Which `loadCredentials()` a read belongs to. Two builders can call it
   * while the first round is in flight; without this the first round's reads
   * would count down the second round's and report it loaded early.
   */
  private round = 0;

  private settle(round: number): void {
    if (round !== this.round) {
      return;
    }
    this.pendingReads = Math.max(0, this.pendingReads - 1);
    if (this.pendingReads === 0) {
      this.credentialsLoaded.set(true);
    }
  }

  /**
   * T.7. The two resume lines the portfolio's opening can start from: the
   * name under Contact and the Summary. Empty strings when the resume has
   * neither, which is most new members.
   */
  public readonly resumeName = signal('');

  /** The resume builder has reported this visit; see `reportResume`. */
  private resumeIsLive = false;

  /**
   * The resume builder's live report: the document as it is on screen. Once
   * it has reported, its word stands for the session: `load()`'s read,
   * landing later, is of the resume as it was when the page opened (or before
   * the last save), and must not replace it.
   */
  public reportResume(document: unknown): void {
    this.resumeIsLive = true;
    this.applyResume(document);
  }
  public readonly resumeSummary = signal('');

  /**
   * T.7. The name and address on the member's account, for the resume's
   * Contact offer. Offered, never written in: the address someone signs in
   * with is not always the one they want an employer to use.
   */
  public readonly accountContact = signal<{ name: string; email: string }>({
    name: '',
    email: ''
  });

  /**
   * Which sections can offer something, for a template that asks.
   *
   * ORDER IS THE PRIORITY, and `dedupe` keeps the first of any repeated title.
   * Richest source first in every case:
   *
   *  - a JST course carries a title, a place AND a year; an ACE row carries a
   *    title and a year; a survey row carries a title alone.
   *  - JST work carries dates; the survey's work is a bare title.
   *  - the resume is a document the member has already written by hand, so for
   *    education and skills it outranks anything derived.
   *
   * Everything the member could be shown is here. The builders decide what to
   * do with it: write it in when the section is empty, offer it when it is not.
   */
  public readonly sources = computed(() => {
    const resume = this.fromResume();
    const profile = this.fromProfile();
    const jstWork = newestFirst(this.jstWork());
    const courses = newestFirst(this.jstCredentials());

    return {
      creds: dedupe([...courses, ...newestFirst(this.aceCredentials())]),
      // A profile MOC the JST already lists by code is the same job twice.
      work: dedupe([
        ...jstWork,
        ...notSameCode(profile['work'] ?? [], jstWork)
      ]),
      edu: this.formalEducation(),
      /*
       * THE RESUME'S EDUCATION OFFER: real schooling first, then the service
       * schools. The resume's Education section takes both ("degrees,
       * certificates and service schools"), but a diploma or a degree must
       * never sit under forty military courses.
       *
       * CPD-683. FROM THE RECORD, NEVER FROM THE RESUME ITSELF. This read
       * `formalEducation()`, which starts with the resume's own Education
       * rows, and the builder reports its rows on every keystroke. So what the
       * member typed came straight back as "Education already on your
       * record", to a member with no record at all, and taking it added the
       * row twice. The profile's degrees, then the JST courses, then the ACE
       * transcript rows (Certifications already offered those; Education
       * offered JST and not ACE).
       */
      resumeEdu: dedupe([
        ...(profile['edu'] ?? []),
        ...courses,
        ...newestFirst(this.aceCredentials())
      ]),
      skills: dedupe([
        ...(resume['skills'] ?? []),
        ...(profile['skills'] ?? [])
      ])
    };
  });

  /**
   * EDUCATION MEANS SCHOOLING: what the member wrote in their resume's
   * Education, and the degrees and diplomas on their profile. JST military
   * courses are Credentials and training, and offering them here as well put
   * the same course in two sections of the public page.
   *
   * A resume seeded before this change can hold JST courses in its Education,
   * so those are held back too.
   */
  private formalEducation(): RecordEntry[] {
    const courses = new Set(
      this.jstCredentials().map((entry) => titleKey(entry.title))
    );
    const written = (this.fromResume()['edu'] ?? []).filter(
      (entry) => !courses.has(titleKey(entry.title))
    );

    return dedupe([...written, ...(this.fromProfile()['edu'] ?? [])]);
  }

  /**
   * T-2. Just the credentials, for a caller that only needs those.
   *
   * Split out of `load()` so the RESUME builder can offer the same ACE rows the
   * portfolio already offers, without also fetching `myResume` -- which is the
   * document the resume builder is itself editing. One read, one shape, one
   * place the mapping from a transcript row to an entry lives.
   *
   * Idempotent enough for two callers: a second fetch overwrites the signal
   * with the same rows. Guarding it would mean caching a read that a fresh
   * upload should invalidate.
   */
  public loadCredentials(): void {
    const round = ++this.round;
    this.pendingReads = 4;
    this.credentialsLoaded.set(false);

    this.ace
      .listTranscripts()
      .pipe(
        map((response) => (response?.success ? response.transcripts : [])),
        catchError(() => of([])),
        tap((transcripts) => {
          // Every row of every saved transcript, most recent upload first.
          const rows = [...transcripts]
            .sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at))
            .flatMap((transcript) => transcript.data?.items ?? []);

          this.aceCredentials.set(
            rows
              .map((row) => ({
                title: (row.credentialTitle ?? '').trim(),
                year: (row.issuedDate ?? '').trim().slice(0, 4),
                note: (row.issuerName ?? '').trim()
              }))
              .filter((entry) => entry.title)
          );
        }),
        finalize(() => this.settle(round))
      )
      .subscribe();

    this.loadJst(round);
    this.loadFromProfile(round);
    this.loadRecordSkills(round);
  }

  /**
   * THE TRANSCRIPT THE REPORTER ACTUALLY UPLOADED.
   *
   * `loadCredentials` above reads `/api/ace/transcripts`, which serves
   * `api.ace_uploads` and nothing else, and is refused outright unless
   * `ACE_TRANSCRIPT_UPLOAD_ENABLED` is on. A **JST** is a different upload,
   * living in different tables behind a different, ungated route.
   *
   * So a member who uploaded a JST got an empty offer twice over, under an
   * Education hint promising "credentials from an uploaded transcript are
   * offered above". The promise was on screen and nothing could satisfy it.
   *
   * Independent of the ACE read on purpose: either source failing must not take
   * the other down with it.
   */
  private loadJst(round: number): void {
    this.jst
      .getLatestJst()
      .pipe(
        map((response) => (response?.success ? response.data : null)),
        catchError(() => of(null)),
        tap((data) => {
          const courses = data?.militaryCourses ?? [];
          const occupations = data?.militaryOccupations ?? [];

          // A course maps onto the Education repeater field for field, which is
          // more than the ACE rows manage: a title, where it was taken, a year.
          this.jstCredentials.set(
            courses
              .map((course) => ({
                title: (course.courseTitle ?? '').trim(),
                place: (course.location ?? '').trim(),
                level: EDUCATION_LEVELS[EDUCATION_LEVELS.length - 1],
                year: (course.endDate ?? course.startDate ?? '')
                  .trim()
                  .slice(0, 4)
              }))
              .filter((entry) => entry.title)
          );

          /*
           * THE ONLY SOURCE IN CPD WITH REAL DATES ON WORK HISTORY.
           *
           * The survey's tables hold a title and a duration in months, with no
           * employer and no dates at all. A JST occupation row has a title, a
           * description and both dates, so it is the one thing that can fill
           * the work repeater honestly.
           */
          const employer = branchAsEmployer(data?.personalInfo?.branch);
          // Still serving, per the transcript itself. Only then does an
          // occupation with no end date mean "current" rather than "unknown".
          const stillServing =
            !!data?.personalInfo &&
            !(data.personalInfo.serviceEnd ?? '').trim();

          this.jstWork.set(
            occupations
              .map((occupation) => {
                const years = [occupation.dateStart, occupation.dateEnd]
                  .map((part) => (part ?? '').trim().slice(0, 4))
                  .filter(Boolean)
                  .join(' to ');
                const description = (occupation.description ?? '').trim();
                const start = toMonth(occupation.dateStart);
                const end = toMonth(occupation.dateEnd);

                return {
                  title: [occupation.mocCode, occupation.mocTitle]
                    .map((part) => (part ?? '').trim())
                    .filter(Boolean)
                    .join(' '),
                  place: years,
                  note: description,
                  ...(employer ? { employer } : {}),
                  ...(start ? { start } : {}),
                  ...(end ? { end } : {}),
                  ...(start && !end && stillServing ? { current: true } : {}),
                  ...(description ? { line: description } : {})
                };
              })
              .filter((entry) => entry.title)
          );
        }),
        finalize(() => this.settle(round))
      )
      .subscribe();
  }

  /**
   * TITLES ONLY, AND THE BLANKS STAY BLANK.
   *
   * What the surveys store is a title and a duration: `usermoc.moctitle`,
   * `userworkexperience.jobtitle`, `usereducation` joined out to a degree
   * title, and `usercompetencies` with literal named skills. There is no
   * employer, no institution and no date anywhere in it.
   *
   * QA.14 read that as "not enough to offer" and stopped. Jackson's call on
   * 21 Sep was the other way: offer the title and leave the rest of the boxes
   * visibly empty. A member who can see their own job title fills the dates in
   * seconds; one staring at a blank page does not start at all.
   *
   * What it must never do is GUESS the missing halves. Inventing an employer
   * from an O*NET title writes fiction into a document somebody sends to an
   * employer, and that is the part QA.14 was right about.
   */
  /**
   * T-2. The learner's real knowledge, skills and abilities.
   *
   * Replaces the technology-product list that `userCompetencies` was feeding
   * the skills offer. Fails soft to nothing: an empty offer is an honest empty
   * state, and a thrown read would take the whole builder with it.
   */
  private loadRecordSkills(round: number): void {
    this.apollo
      .query<{ myRecordSkills?: string[] | null }>({ query: MY_RECORD_SKILLS })
      .pipe(
        map((r) => r?.data?.myRecordSkills ?? []),
        catchError(() => of([] as string[])),
        finalize(() => this.settle(round))
      )
      .subscribe((names) =>
        this.fromProfile.update((held) => ({
          ...held,
          skills: names.filter(Boolean).map((title) => ({ title }))
        }))
      );
  }

  private loadFromProfile(round: number): void {
    // T.7. A session with no profile yet never emits one, and this read has to
    // count as finished for `credentialsLoaded` to. After three seconds, the
    // figure the token wait uses, it counts as finished, but a profile that
    // arrives later is still applied: the offers fill in when it does.
    let counted = false;
    const countOnce = () => {
      if (!counted) {
        counted = true;
        this.settle(round);
      }
    };
    const wait = setTimeout(countOnce, 3000);

    this.userProfileFacade.userProfile$
      .pipe(
        filter((profile) => !!profile),
        take(1),
        catchError(() => of(null)),
        tap((profile) => {
          const p = (profile ?? {}) as {
            firstName?: string | null;
            lastName?: string | null;
            userEmail?: string | null;
            userMocs?:
              | ({ mocCode?: string; mocTitle?: string } | null)[]
              | null;
            userWorkExperiences?:
              | ({ occupationTitle?: string } | null)[]
              | null;
            userEducations?:
              | ({
                  educationLevelShortDescr?: string;
                  degreeInfo?: { title?: string } | null;
                  completed?: boolean | null;
                } | null)[]
              | null;
            userCompetencies?: {
              sourceExperience?:
                | ({
                    coreCompetencies?:
                      | ({ competencyLabel?: string } | null)[]
                      | null;
                  } | null)[]
                | null;
            } | null;
          };

          // No `place`, no `year`: those boxes stay empty for the member to
          // fill, because nothing in the record knows them.
          const titles = profileWorkTitles(p.userMocs, p.userWorkExperiences);
          const work = titles.map((title) => ({ title }));

          // The level travels with the program now: taking the program's title
          // alone dropped "Some College Courses" off "Cyber/Computer Forensics".
          // `completed: false` is schooling still under way.
          const edu: RecordEntry[] = (p.userEducations ?? [])
            .map((row) => {
              const level = educationLevelFor(row?.educationLevelShortDescr);
              const title = (
                row?.degreeInfo?.title ??
                row?.educationLevelShortDescr ??
                ''
              ).trim();
              return {
                title,
                ...(level ? { level } : {}),
                ...(row?.completed === false ? { year: IN_PROGRESS } : {})
              };
            })
            .filter((entry) => entry.title);

          /*
           * SKILLS NO LONGER COME FROM HERE.
           *
           * `userCompetencies` is built by joining api.onet_technical_skills,
           * a table of technology PRODUCTS, so this offered "Jenkins CI",
           * "Extensible markup language XML" and "Building automation
           * software" as the skills to put on a resume. They are tools an
           * occupation may use, not things a person is good at.
           *
           * The real knowledge, skills and abilities come from the scored
           * SkillsDNA spine, read by `loadRecordSkills` below, which is the
           * same source the occupation page's KSA list uses.
           */
          this.fromProfile.update((held) => ({ ...held, work, edu }));

          this.accountContact.set({
            name: [p.firstName, p.lastName]
              .map((part) => (part ?? '').trim())
              .filter(Boolean)
              .join(' '),
            email: (p.userEmail ?? '').trim()
          });
        }),
        finalize(() => {
          clearTimeout(wait);
          countOnce();
        })
      )
      .subscribe();
  }

  /**
   * T-2. The credentials on file, for a caller that is not the portfolio.
   *
   * BOTH transcript kinds, JST first. This read the ACE signal alone, which is
   * exactly why a member who had uploaded a JST saw nothing underneath a hint
   * promising their transcript's credentials were offered above.
   */
  public readonly recordCredentials = computed(() =>
    dedupe([...this.jstCredentials(), ...this.aceCredentials()])
  );

  public load(): void {
    this.loadCredentials();
    this.readResume();
  }

  /**
   * WALK-44 review, LOW 12. The member switched to another of their resumes:
   * what was reported for the last one no longer stands, so the current one
   * is read again (and the builder's live report of it replaces that read).
   */
  public rereadResume(): void {
    this.resumeIsLive = false;
    this.readResume();
  }

  private readResume(): void {
    /*
     * WAIT FOR THE TOKEN, like every other permission-gated read. This one was
     * missed when its siblings were gated, and it is the read that still went
     * out with no Authorization header on a signed-in load: measured on
     * /transition with a real session, `MyResume (no auth header)` was the
     * only remaining denial once the feature flags were gated.
     *
     * It matters beyond this service. The interceptor reads a 403 as an
     * expired session, so an ungated read anywhere is a trigger for the
     * teardown, whoever owns it.
     */
    this.authFacade.authToken$
      .pipe(
        filter((token) => !!token),
        take(1),
        timeout({ first: 3000, with: () => of('') }),
        switchMap(() =>
          this.myResumeGQL.fetch({}, { fetchPolicy: 'network-only' })
        )
      )
      .pipe(
        map((result) => result?.data?.myResume?.document ?? null),
        catchError(() => of(null)),
        tap((document) => {
          if (!this.resumeIsLive) {
            this.applyResume(document);
          }
          this.hasLoaded.set(true);
        })
      )
      .subscribe();
  }

  /**
   * T.8. What the portfolio shows from the resume: experience, education,
   * skills, the name and the summary, from a resume document.
   *
   * `load()` reads it once, and the Transition page builds all four tabs when
   * it opens, so that one read is of the resume as it was then. The resume
   * builder calls this as the member edits, so an upload, a Via answer or a
   * typed name reaches the portfolio in the same visit, not after a reload.
   */
  public applyResume(document: unknown): void {
    const doc = (document ?? {}) as {
      fields?: Record<string, Record<string, string>>;
      rows?: Record<string, Record<string, string>[]>;
      lines?: Record<string, string[]>;
      items?: Record<string, string[]>;
    };

    this.experience.set(experienceFromResume(doc));

    const education = (doc.rows?.['edu'] ?? [])
      .filter((row) => (row['credential'] ?? '').trim())
      .map((row) => ({
        title: educationTitle(row['credential'] ?? '', row['level']),
        place: (row['institution'] ?? '').trim(),
        year: (row['year'] ?? '').trim()
      }));

    const skills = (doc.items?.['skills'] ?? [])
      .map((skill) => ({ title: skill.trim() }))
      .filter((entry) => entry.title);

    this.fromResume.set({ edu: education, skills });
    this.resumeName.set((doc.fields?.['contact']?.['name'] ?? '').trim());
    this.resumeSummary.set((doc.fields?.['summary']?.['summary'] ?? '').trim());
  }

  /**
   * What a section can still take. Anything already on the page is dropped,
   * because an offer to add something twice is not an offer.
   */
  public offer(key: string, held: Record<string, string>[]): RecordEntry[] {
    const taken = new Set(
      held.map((entry) => (entry['title'] ?? '').trim().toLowerCase())
    );

    return (this.sources()[key as 'creds'] ?? []).filter(
      (entry) => !taken.has(entry.title.toLowerCase())
    );
  }
}
